import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Container, Card, Form, Button, Alert, Spinner, Badge } from 'react-bootstrap';
import logo from '../../../../images/logo.png';
import { getCompanyInfo, getOrderByUuid } from '../../../../services/PublicOrderService';
import './PublicOrder.css';
import PublicBrandSignature from '../../../components/Common/PublicBrandSignature';

const formatCurrency = (value) =>
    (value || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const formatCpf = (value) => {
    const digits = value.replace(/\D/g, '').slice(0, 11);
    return digits
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
};

const resolvePublicAssetUrl = (url) => {
    if (!url) return null;
    if (/^https?:\/\//i.test(url)) return url;
    return `${process.env.REACT_APP_API_URL}${url.startsWith('/') ? url : `/${url}`}`;
};

const statusLabel = {
    IN: 'Recebido',
    PREPARING: 'Em preparo',
    PREPARED: 'Pronto',
    DELIVERING: 'Em entrega',
    DELIVERED: 'Entregue',
    REJECTED: 'Rejeitado',
};

const paymentStatusLabel = {
    WAITING: 'Aguardando pagamento',
    PAID: 'Pago',
    APPROVED: 'Aprovado',
    IDENTIFIED: 'Pagamento identificado',
    UNPAID: 'Não pago',
    CANCELED: 'Cancelado',
    EXPIRED: 'Expirado',
};

const getRemainingSeconds = (expiresAt) => {
    if (!expiresAt) return null;
    return Math.max(0, Math.floor((new Date(expiresAt).getTime() - Date.now()) / 1000));
};

const formatRemaining = (seconds) => {
    if (seconds === null) return '';
    const minutes = Math.floor(seconds / 60).toString().padStart(2, '0');
    const restSeconds = (seconds % 60).toString().padStart(2, '0');
    return `${minutes}:${restSeconds}`;
};

const PublicOrderTrackPage = () => {
    const { companyUuid } = useParams();
    const [code, setCode] = useState('');
    const [document, setDocument] = useState('');
    const [companyInfo, setCompanyInfo] = useState(null);
    const [order, setOrder] = useState(null);
    const [loading, setLoading] = useState(false);
    const [loadingCompany, setLoadingCompany] = useState(true);
    const [error, setError] = useState('');
    const [pixRemainingSeconds, setPixRemainingSeconds] = useState(null);
    const progressStatuses = ['IN', 'PREPARING', 'PREPARED', 'DELIVERING', 'DELIVERED'];
    const currentProgressIndex = progressStatuses.indexOf(order?.status);
    const pixExpired = order?.pixCopyPaste && pixRemainingSeconds === 0;

    useEffect(() => {
        let active = true;
        getCompanyInfo(companyUuid)
            .then((response) => {
                if (active) setCompanyInfo(response.data);
            })
            .catch(() => {
                if (active) setCompanyInfo(null);
            })
            .finally(() => {
                if (active) setLoadingCompany(false);
            });

        return () => {
            active = false;
        };
    }, [companyUuid]);

    useEffect(() => {
        if (!order?.pixExpiresAt) {
            setPixRemainingSeconds(null);
            return undefined;
        }

        const updateRemaining = () => setPixRemainingSeconds(getRemainingSeconds(order.pixExpiresAt));
        updateRemaining();
        const timer = window.setInterval(updateRemaining, 1000);
        return () => window.clearInterval(timer);
    }, [order?.pixExpiresAt]);

    const handleSubmit = async (event) => {
        event.preventDefault();

        const documentDigits = document.replace(/\D/g, '');
        if (!code.trim() || documentDigits.length !== 11) {
            setError('Informe o código do pedido e o CPF.');
            return;
        }

        try {
            setLoading(true);
            setError('');
            const response = await getOrderByUuid(companyUuid, code.trim(), documentDigits);
            setOrder(response.data);
        } catch (err) {
            setOrder(null);
            setError(err.response?.data?.message || 'Pedido não encontrado. Verifique o código e CPF.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="public-order-page">
            <div
                className="order-hero"
                style={companyInfo?.headerColor ? { background: companyInfo.headerColor } : undefined}
            >
                <div className="order-hero-logo">
                    {companyInfo?.logoUrl ? (
                        <img src={resolvePublicAssetUrl(companyInfo.logoUrl)} alt={companyInfo.name} />
                    ) : (
                        <img src={logo} alt="Logo" />
                    )}
                </div>
                <h1 className="order-hero-name">{companyInfo?.name || 'Acompanhar pedido'}</h1>
                <p className="order-hero-sub">Acompanhe seu pedido</p>
            </div>

            <Container className="py-4 booking-container">
                <Card className="booking-card shadow-lg mb-3">
                    <Card.Body className="p-4">
                        {loadingCompany && (
                            <div className="text-center py-2 text-muted">
                                <Spinner size="sm" className="me-2" />
                                Carregando...
                            </div>
                        )}
                        <Form onSubmit={handleSubmit}>
                            <div className="step-header mb-3">
                                <h4 className="step-title mb-1">
                                    <i className="fa fa-search me-2 text-primary"></i>
                                    Consultar pedido
                                </h4>
                                <p className="step-subtitle text-muted">
                                    Use o código exibido ao finalizar e o CPF informado no cadastro.
                                </p>
                            </div>

                            <div className="order-track-search-panel">
                                <Form.Group>
                                    <Form.Label className="fw-semibold">Código do pedido</Form.Label>
                                    <div className="order-track-input">
                                        <i className="fa fa-receipt"></i>
                                        <Form.Control
                                            value={code}
                                            onChange={(event) => setCode(event.target.value.toUpperCase())}
                                            placeholder="Ex: ABC12345"
                                            autoCapitalize="characters"
                                        />
                                    </div>
                                </Form.Group>

                                <Form.Group>
                                    <Form.Label className="fw-semibold">CPF</Form.Label>
                                    <div className="order-track-input">
                                        <i className="fa fa-id-card"></i>
                                        <Form.Control
                                            value={document}
                                            onChange={(event) => setDocument(formatCpf(event.target.value))}
                                            placeholder="000.000.000-00"
                                            inputMode="numeric"
                                        />
                                    </div>
                                </Form.Group>
                            </div>

                            {error && (
                                <Alert variant="danger" className="mt-3 mb-0">
                                    <i className="fa fa-exclamation-circle me-2"></i>
                                    {error}
                                </Alert>
                            )}

                            <div className="order-track-actions mt-3">
                                <Button variant="primary" type="submit" disabled={loading} className="order-track-submit">
                                    {loading ? (
                                        <><Spinner size="sm" className="me-2" />Buscando...</>
                                    ) : (
                                        <><i className="fa fa-search me-2"></i>Consultar pedido</>
                                    )}
                                </Button>
                                <Button as={Link} to={`/order/${companyUuid}`} variant="outline-secondary">
                                    <i className="fa fa-plus me-2"></i>
                                    Novo pedido
                                </Button>
                            </div>
                        </Form>
                    </Card.Body>
                </Card>

                {order && (
                    <Card className="booking-card shadow-lg order-track-result">
                        <Card.Body className="p-4">
                            <div className="order-track-header mb-3">
                                <div>
                                    <small className="text-muted">Pedido</small>
                                    <h4 className="mb-0 font-monospace">{order.uuid?.substring(0, 8).toUpperCase()}</h4>
                                </div>
                                <Badge bg="primary">{statusLabel[order.status] || order.status}</Badge>
                            </div>

                            <div className="order-track-status-grid mb-3">
                                <div className="summary-card order-track-section">
                                    <small className="text-muted d-block mb-1">Status do pedido</small>
                                    <strong>{statusLabel[order.status] || order.status}</strong>
                                </div>
                                <div className="summary-card order-track-section">
                                    <small className="text-muted d-block mb-1">Pagamento</small>
                                    <strong>{paymentStatusLabel[order.paymentStatus] || order.paymentStatus || '-'}</strong>
                                </div>
                            </div>

                            <div className="order-track-progress mb-3">
                                {progressStatuses.map((status, index) => (
                                    <div
                                        key={status}
                                        className={`order-track-progress-step${index <= currentProgressIndex ? ' active' : ''}`}
                                    >
                                        <span></span>
                                        <small>{statusLabel[status]}</small>
                                    </div>
                                ))}
                            </div>

                            {(order.paymentUrl || order.pixCopyPaste) && (
                                <Card className="public-payment-card text-start mb-3">
                                    <Card.Body>
                                        <h6 className="fw-bold mb-3">Pagamento</h6>
                                        {order.paymentUrl && (
                                            <Button
                                                variant="success"
                                                className="w-100"
                                                onClick={() => window.open(order.paymentUrl, '_blank', 'noopener,noreferrer')}
                                            >
                                                Pagar agora
                                            </Button>
                                        )}
                                        {order.pixCopyPaste && (
                                            <>
                                                {pixExpired ? (
                                                    <Alert variant="warning" className="mb-0">
                                                        <i className="fa fa-clock me-2"></i>
                                                        Este Pix expirou. Faça um novo pedido para gerar outro pagamento.
                                                    </Alert>
                                                ) : (
                                                    <>
                                                        {pixRemainingSeconds !== null && (
                                                            <div className="pix-expiration-box mb-3">
                                                                <span>Tempo para pagamento</span>
                                                                <strong>{formatRemaining(pixRemainingSeconds)}</strong>
                                                            </div>
                                                        )}
                                                        {order.pixQrCode && (
                                                            <div className="public-pix-qrcode mb-3">
                                                                <img src={order.pixQrCode} alt="QR Code Pix" />
                                                            </div>
                                                        )}
                                                        <code className="public-pix-code d-block mb-3">{order.pixCopyPaste}</code>
                                                        <Button
                                                            variant="success"
                                                            className="w-100"
                                                            onClick={() => navigator.clipboard.writeText(order.pixCopyPaste)}
                                                        >
                                                            Copiar código Pix
                                                        </Button>
                                                    </>
                                                )}
                                            </>
                                        )}
                                    </Card.Body>
                                </Card>
                            )}

                            <div className="summary-card order-track-section">
                                <h6 className="fw-bold mb-3">Itens</h6>
                                {(order.items || []).map((item) => (
                                    <div key={item.id || item.productId || item.name} className="d-flex justify-content-between border-bottom py-2">
                                        <span>{item.qty}x {item.productName || item.name}</span>
                                        <strong>{formatCurrency(item.total || ((item.price || 0) * (item.qty || 0)))}</strong>
                                    </div>
                                ))}
                                <div className="d-flex justify-content-between pt-3">
                                    <span className="fw-bold">Total</span>
                                    <span className="fw-bold text-primary">{formatCurrency(order.total)}</span>
                                </div>
                            </div>
                        </Card.Body>
                    </Card>
                )}

                <PublicBrandSignature />
            </Container>
        </div>
    );
};

export default PublicOrderTrackPage;
